import React, { useState, useRef, useEffect } from 'react';
import {
  Download, FileText, Link2, Mail, ChevronDown, Check, Copy
} from 'lucide-react';

const ShareExportMenu = ({ onExportPDF, onExportCSV, shareUrl, shareTitle, selectedCount }) => {
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const copyLink = () => {
    const url = shareUrl || window.location.href;
    navigator.clipboard.writeText(url).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  const emailShare = () => {
    const subject = encodeURIComponent(shareTitle || 'Total Composites - Admin Report');
    const body = encodeURIComponent(`Here is the link to the admin dashboard:\n\n${shareUrl || window.location.href}`);
    window.open(`mailto:?subject=${subject}&body=${body}`, '_self');
    setOpen(false);
  };

  const label = selectedCount > 0 ? `Export (${selectedCount})` : 'Export / Share';

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen(!open)}
        className="flex items-center gap-2 px-4 py-2.5 border border-gray-200 rounded-xl text-xs font-bold text-gray-600 hover:border-gray-300 transition-all"
        data-testid="share-export-btn"
      >
        <Download size={13} />
        {label}
        <ChevronDown size={12} className={`transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <div className="absolute right-0 top-full mt-1 bg-white rounded-xl shadow-xl border border-gray-200 w-56 z-50 overflow-hidden" data-testid="share-export-menu">
          {/* Export Section */}
          <div className="px-3 pt-2 pb-1">
            <p className="text-[9px] font-bold uppercase tracking-widest text-gray-400">Export</p>
          </div>
          {onExportPDF && (
            <button
              onClick={() => { onExportPDF(); setOpen(false); }}
              className="w-full flex items-center gap-2.5 px-3 py-2 text-xs font-semibold text-gray-700 hover:bg-gray-50 transition-colors"
              data-testid="export-pdf-btn"
            >
              <FileText size={13} className="text-red-500" />
              Export as PDF {selectedCount > 0 ? `(${selectedCount})` : ''}
            </button>
          )}
          {onExportCSV && (
            <button
              onClick={() => { onExportCSV(); setOpen(false); }}
              className="w-full flex items-center gap-2.5 px-3 py-2 text-xs font-semibold text-gray-700 hover:bg-gray-50 transition-colors"
              data-testid="export-csv-btn"
            >
              <FileText size={13} className="text-green-500" />
              Export as CSV {selectedCount > 0 ? `(${selectedCount})` : ''}
            </button>
          )}

          {/* Share Section */}
          <div className="border-t border-gray-100 px-3 pt-2 pb-1 mt-1">
            <p className="text-[9px] font-bold uppercase tracking-widest text-gray-400">Share</p>
          </div>
          <button
            onClick={() => { copyLink(); }}
            className="w-full flex items-center gap-2.5 px-3 py-2 text-xs font-semibold text-gray-700 hover:bg-gray-50 transition-colors"
            data-testid="copy-link-btn"
          >
            {copied ? <Check size={13} className="text-green-500" /> : <Link2 size={13} className="text-blue-500" />}
            {copied ? 'Link Copied!' : 'Copy Share Link'}
          </button>
          <button
            onClick={emailShare}
            className="w-full flex items-center gap-2.5 px-3 py-2 text-xs font-semibold text-gray-700 hover:bg-gray-50 transition-colors rounded-b-xl"
            data-testid="email-share-btn"
          >
            <Mail size={13} className="text-orange-500" />
            Share via Email
          </button>
        </div>
      )}
    </div>
  );
};

export default ShareExportMenu;
